// How a session ended, as the viewer should hear it.
//
// Three endings the player tells apart, each with its own card:
//
//   'closed'  — the VIEWER asked for it: the embedding page sent the
//               terminateSession control message (the SDK's Close), or the
//               player's own page-leave path (pagehide) ran and the page came
//               back from the bfcache. Quiet
//               "Stream closed" card, no auto-retry, one "Start again" action
//               (a fresh session). Decided ONLY by the player's own memory of
//               asking (App.js viewerClosed, set by terminateSession and by the
//               page-leave path) — never by the echoed 1000 "viewer-closed"
//               frame alone; see isViewerClosed.
//   'lost'    — the GPU host or the Unreal app went away mid-session. Signalling
//               tells the viewer with ONE wire contract on every path (a closed
//               streamer socket, or the platform ending the session underneath a
//               streamer socket that never closed): the `streamerDisconnected`
//               protocol message, then a WebSocket close with code 4006 and
//               reason "Streamer Disconnected". The session is already over
//               server-side — there is nothing to reconnect to — so the player
//               shows "Connection lost" with a Retry that starts a FRESH
//               session, plus ONE automatic retry per page life.
//   'gave-up' — the stream never came: the player waited for the streamer and
//               it did not answer (the 60 s post-answer classifier's
//               "streamer-mute", the pre-offer backstop, or signalling's 4005
//               "Streamer connection timeout"). Same "Connection lost" card and
//               Retry; NEVER an automatic retry — the viewer has already waited,
//               and an auto-retry that lands on the same rebooting host would
//               only make them wait again (seen 2026-10-23 on the dedicated test
//               platform: loss → auto-retry → placed on the host still rebooting
//               → "GAVE UP WAITING FOR <id>" with no button).
//   'refused' — signalling refused admission (4401 "admission required": the
//               first-message admission ticket was missing or not accepted —
//               see libs/streampixel/src/admission.js). Retrying presents the
//               same refused credentials, so the card has no button and
//               nothing reconnects; a page reload mints a new ticket.
//   'other'   — everything else keeps the close handler's existing wording.
//
// 4006 with reason "No streamer connected" is a different event (an SFU viewer
// whose host stream never appeared within 3 minutes), and in local dev mode
// 4006 means "the editor restarted, re-attach" — neither is a loss.
//
// CommonJS on purpose: imported by App.js and DisconnectScreen.js through
// webpack AND run by plain node in test/streamer-loss.js (the repo's jest setup
// cannot run — see there).

const STREAMER_LOST_CODE = 4006;
const STREAMER_LOST_REASON = 'Streamer Disconnected';
// What the player itself sends when it ends a session on purpose (App.js
// closeSignallingExplicitly: terminateSession, page leave, the player's own
// give-up); the `ws` server echoes the frame, so the close event usually carries
// it back. Classification never relies on the echo — every sender records why
// it closed first (classifyClose's `viewerClosed` / `gaveUp`).
const VIEWER_CLOSED_CODE = 1000;
const VIEWER_CLOSED_REASON = 'viewer-closed';
// Signalling's READY_TO_SUBSCRIBE wait ran out (180 s) — the streamer never
// connected. Carries reason "Streamer connection timeout after <n>s".
const STREAMER_TIMEOUT_CODE = 4005;
// Signalling refused admission: the {"type":"admission","ticket"} first message
// was missing, late or invalid (ticket-not-in-URL mode, P-39). Terminal.
const ADMISSION_REQUIRED_CODE = 4401;
// The player's own backstop for the wait between "connected to signalling" and
// the first SDP offer, for the case where signalling never says anything at
// all. Signalling's own wait is 180 s, but it starts at placement
// (readyToSubscribe), not at connect, and signalling tells the player nothing
// when that happens — so this starts earlier than the server's and allows two
// minutes of placement on top of its 180 s, letting the server's 4005 (with
// its reason) win whenever signalling is healthy. A placement slower than that
// with no queue position would still be cut short; the real fix is a "placed"
// message from signalling to arm this from. CLEARED, not re-armed, by a
// queue-position message: a queued viewer is waiting on purpose, positions are
// only re-sent when a seat is released (ACM release.lua), and the server owns
// the timeout once the viewer is placed.
const STREAMER_WAIT_BACKSTOP_MS = 300000;
// One automatic fresh-session attempt, shortly after the loss, while the tab is
// visible; after that the viewer presses Retry. The delay is JITTERED: a host
// dying takes every viewer on it down in the same second, and a fixed delay
// would send all of them to the ticket mint and the scheduler in the same
// second again (the scheduler places over cap when a region is full, so a
// same-second burst lands on one GPU).
const STREAMER_LOST_AUTO_RETRY_MIN_MS = 2000;
const STREAMER_LOST_AUTO_RETRY_MAX_MS = 6000;

const CLOSE_KIND = Object.freeze({
  CLOSED: 'closed',
  LOST: 'lost',
  GAVE_UP: 'gave-up',
  REFUSED: 'refused',
  OTHER: 'other',
});

function streamerLostAutoRetryDelay(random = Math.random) {
  const r = Math.min(Math.max(Number(random()) || 0, 0), 1);
  return Math.round(STREAMER_LOST_AUTO_RETRY_MIN_MS + r * (STREAMER_LOST_AUTO_RETRY_MAX_MS - STREAMER_LOST_AUTO_RETRY_MIN_MS));
}

function isStreamerLost(code, reason, { devMode = false } = {}) {
  if (devMode) return false;
  return Number(code) === STREAMER_LOST_CODE && String(reason || '').trim() === STREAMER_LOST_REASON;
}

// Whether the viewer asked for this close. The player's own memory decides,
// whatever code the close event carries (the echo may come back without a
// reason, or a dying socket may report 1006). The echoed 1000 "viewer-closed"
// ALONE is not enough: every path that sends it records why first
// (terminateSession and page leave set viewerClosed, the player's give-up sets
// gaveUp), so an echo with no record behind it is not something the viewer
// did — it keeps the neutral generic card, as before the "Stream closed" card
// existed. `code` and `reason` stay in the signature for the call sites.
function isViewerClosed(code, reason, { viewerClosed = false } = {}) {
  return viewerClosed === true;
}

// The decision for a signalling close event. `viewerClosed`: the player itself
// asked for this close (terminateSession, or the page-leave path). `gaveUp`:
// the player already ended the attempt on its own timeout and is now closing
// the socket it was waiting on — that close is the player's doing too, not a
// new event. Precedence: the player's own give-up first (it closes the socket
// with the same 1000 "viewer-closed" frame), then viewer intent (a close the
// viewer asked for is never "lost", whatever code arrives), then the wire codes.
function classifyClose({ code, reason, viewerClosed = false, gaveUp = false, devMode = false } = {}) {
  if (gaveUp) return CLOSE_KIND.GAVE_UP;
  if (isViewerClosed(code, reason, { viewerClosed })) return CLOSE_KIND.CLOSED;
  if (Number(code) === ADMISSION_REQUIRED_CODE) return CLOSE_KIND.REFUSED;
  if (isStreamerLost(code, reason, { devMode })) return CLOSE_KIND.LOST;
  if (!devMode && Number(code) === STREAMER_TIMEOUT_CODE) return CLOSE_KIND.GAVE_UP;
  return CLOSE_KIND.OTHER;
}

// The viewer-facing line. DisconnectScreen prefixes it with the close code for
// support ("Disconnected code:4006. …") — see composeStreamerLostMessage.
// `retryFailed`: the Retry itself could not get a new session (the mint or the
// access lookup failed — offline, 429, 5xx). The viewer keeps the Retry button
// rather than landing on the "unavailable" card, which has none.
function streamerLostMessage({ autoRetryPending = false, retryFailed = false } = {}) {
  if (retryFailed) return 'Connection to the stream was lost, and a new session could not be started yet. Retry in a moment.';
  return autoRetryPending
    ? 'Connection to the stream was lost. Starting a new session in a moment…'
    : 'Connection to the stream was lost. Retry to start a new session.';
}

function composeStreamerLostMessage({ autoRetryPending = false, retryFailed = false } = {}) {
  return `Disconnected code:${STREAMER_LOST_CODE}. ${streamerLostMessage({ autoRetryPending, retryFailed })}`;
}

// The one line under "Stream closed". `leftPage`: the close came from the
// player's own page-leave path (pagehide) and the page is still here — it was
// restored from the bfcache. They did not press Close, so the line must not
// say they did. (beforeunload is deliberately NOT a page-leave signal: it also
// fires for a download link, a cancelled navigation and "Stay" on a leave
// prompt, none of which leaves the page.)
function closedByViewerMessage({ leftPage = false } = {}) {
  return leftPage ? 'The stream was closed when you left the page.' : 'You closed the stream.';
}

// The "Start again"/Retry on a closed or gave-up card could not get a new
// session for a reason a later press can fix. Kind-neutral: a viewer who
// pressed Close on purpose must not be told the connection was lost (the lost
// card has its own line, streamerLostMessage({ retryFailed })).
function retryFailedMessage(kind) {
  if (kind === CLOSE_KIND.LOST) return streamerLostMessage({ retryFailed: true });
  return 'A new session could not be started yet. Try again in a moment.';
}

// The one line under "Connection lost" when the stream never came. `why` is the
// player's own classification, for the small detail line: 'streamer-mute' (an
// answer, then no media and no remote candidates for 60 s), 'no-offer' (the
// backstop: nothing from signalling for STREAMER_WAIT_BACKSTOP_MS),
// 'server-timeout' (4005).
function gaveUpMessage() {
  return 'The stream did not respond. Retry to start a new session.';
}

// The one line under "Access refused" (4401). No Retry: the same ticket would
// be refused again, and a ticket the server will not take is not something a
// fresh mint from the same page state is likely to fix without a reload.
function admissionRefusedMessage() {
  return 'The stream did not accept this session\'s access ticket. Reload the page to request a new session.';
}

function gaveUpDetail(why, code) {
  if (code) return `Close code ${code}`;
  if (why === 'streamer-mute') return 'No answer from the streamer within 60 s';
  if (why === 'no-offer') return 'No streamer within 5 min';
  return undefined;
}

// What DisconnectScreen draws for each kind: title, the line under it, the
// button's label, and whether the viewer ever sees a close code. `subtitle`
// is the kind's default; the card may replace it with a more specific line
// (retry-failed, the 4005 reason) when App.js supplies one.
function disconnectCardCopy(kind) {
  switch (kind) {
    case CLOSE_KIND.CLOSED:
      return { title: 'Stream closed', subtitle: closedByViewerMessage(), action: 'Start again', showCode: false };
    case CLOSE_KIND.GAVE_UP:
      return { title: 'Connection lost', subtitle: gaveUpMessage(), action: 'Retry', showCode: true };
    case CLOSE_KIND.LOST:
      return { title: 'Connection lost', subtitle: streamerLostMessage(), action: 'Retry', showCode: true };
    case CLOSE_KIND.REFUSED:
      return { title: 'Access refused', subtitle: admissionRefusedMessage(), action: null, showCode: true };
    default:
      return { title: 'Connection lost', subtitle: 'The stream disconnected. Reconnect to carry on watching.', action: 'Reconnect', showCode: true };
  }
}

// Whether an ending earns the ONE automatic fresh-session attempt. Only a loss
// does, only once per page life, and only while the tab is visible (a hidden
// tab's retry would mint a session nobody is watching). A give-up never does.
function shouldAutoRetry(kind, { alreadyAutoRetried = false, visible = true } = {}) {
  return kind === CLOSE_KIND.LOST && !alreadyAutoRetried && visible;
}

// What the embedder's terminateSession (the SDK's Close) does, given what is
// up at that moment:
//   'socket'      — a signalling socket is open (or opening): closing it is the
//                   whole job; the close handler then draws "Stream closed" and
//                   tells the embedder (disconnected, then closed).
//   'closed-card' — no socket, but the Close CANCELS something that would
//                   otherwise go on: the one automatic retry (`autoRetryPending`),
//                   a reconnect waiting out its delay (`reconnectPending`), or a
//                   fresh session being started with no player built yet
//                   (`startPending`: the ticket mint or the 1 s connect delay of a
//                   first load, a Retry, a "Start again" or the auto-retry). The
//                   player draws "Stream closed" itself and posts 'closed'.
//   'ack'         — a terminal card is already up and nothing is pending: the
//                   session was over before the Close, so the card that says WHY
//                   (inactivity, no server, connection lost, could not start)
//                   stays — "You closed the stream." would misattribute the
//                   ending — and the embedder still hears 'closed'.
//   'none'        — nothing to close (the password gate, the sign-in card, the
//                   unavailable card).
function terminateAction({
  socketWillClose = false, autoRetryPending = false, reconnectPending = false,
  startPending = false, cardUp = false,
} = {}) {
  if (socketWillClose) return 'socket';
  if (autoRetryPending || reconnectPending || startPending) return 'closed-card';
  if (cardUp) return 'ack';
  return 'none';
}

// Whether the Retry / "Start again" after this ending keeps a pinned test
// session's pin (ops "Test": share/<projectId>?pin=<pinId>). After a give-up
// the pinned host not answering IS the thing under test, and after a Close the
// host is fine: the fresh session mints again with the same pin and lands on
// the same host (a pin allows several mints; a spent, expired or revoked one is
// refused by the mint). After a LOSS — or anything unknown — the pin is dropped
// as before: the pinned host is the one that just died, a pinned session is
// never placed anywhere else, so that retry is an ordinary session on whatever
// host the scheduler picks. `hostLost`: the attempt being replaced ended in a
// loss even though the card now says something else (a Close cancelled the
// auto-retry and put "Stream closed" over the loss card) — the host is still
// the dead one, so the pin goes.
function keepsPinOnRetry(kind, { hostLost = false } = {}) {
  if (hostLost) return false;
  return kind === CLOSE_KIND.GAVE_UP || kind === CLOSE_KIND.CLOSED;
}

// A fresh-session attempt that failed for a reason a later Retry can fix:
// the request never left (offline), the mint is rate-limited, or the backend
// errored. 4xx otherwise (403 refused, 404 gone, 410 withdrawn) will not.
function isRetryableTicketFailure(status) {
  const s = Number(status) || 0;
  return s === 0 || s === 408 || s === 429 || s >= 500;
}

// The post-SDP-answer classifier: the streamer answered, and 30 s later the
// connection is still not up. Why, from the same diagnostics the player logs
// (utils/webRtcDiagnostics). Order matters (validated against 20 days of
// production timeouts): a client that gathered nothing often ALSO sees zero
// remotes — its broken network must win over "streamer mute". Anything
// ambiguous has no message. 'streamer-mute' has none either: it is the
// player's own give-up (the "Connection lost" card), not a line of text.
const NOTHING_GATHERED_MESSAGE = 'Your network is blocking the connection entirely. Please try a different network.';
const NETWORK_BLACKHOLE_MESSAGE = 'Your network appears to block video streaming traffic. Try a different network — a mobile hotspot usually works — or ask IT to allow WebRTC.';

function isConnectionUp(d = {}) {
  return d.iceConnectionState === 'connected' || d.iceConnectionState === 'completed' || d.connectionState === 'connected';
}

function remoteCandidateCount(d = {}) {
  return Array.isArray(d.remoteCandidates) ? d.remoteCandidates.length : d.remoteCandidates;
}

function classifyConnectTimeout(d = {}) {
  const remotes = remoteCandidateCount(d);
  const bytes = d.transport ? d.transport.bytesReceived : null;
  if (d.turnCandidatesFound === false && d.srflxCandidatesFound === false) {
    return { failureClass: 'nothing-gathered', failureMessage: NOTHING_GATHERED_MESSAGE };
  }
  if (remotes === 0) return { failureClass: 'streamer-mute', failureMessage: null };
  if ((d.candidatePairs || 0) > 0 && (d.connectedPairs || 0) === 0 && bytes === 0 && d.turnCandidatesFound === true) {
    return { failureClass: 'network-blackhole', failureMessage: NETWORK_BLACKHOLE_MESSAGE };
  }
  if (bytes > 0) return { failureClass: 'progressed-then-stalled', failureMessage: null };
  return { failureClass: 'unclassified', failureMessage: null };
}

// Whether the +30 s classification is worth a verdict 30 s later: the two
// network explanations (shown as text) and the mute streamer (a give-up).
function armsConnectVerdict(first = {}) {
  return first.failureClass === 'streamer-mute' || !!first.failureMessage;
}

// The +60 s verdict on a +30 s classification, from FRESH diagnostics `d2`:
//   { action: 'none' }               — connected meanwhile, or nothing to say
//   { action: 'message', message }   — show the network explanation as text
//                                      (never ends the attempt: if the
//                                      connection lands, the stream takes over)
//   { action: 'give-up' }            — end the attempt ("Connection lost" + Retry)
// A mute streamer is given up on only if it is STILL mute: remote candidates
// that arrived between +30 s and +60 s (a slow TURN allocation on the host)
// mean the streamer is answering and ICE may yet connect — ending that session
// would kill a stream seconds from starting. Unknown remotes (diagnostics
// unreadable) keep the +30 s verdict.
function connectTimeoutVerdict(first = {}, d2 = {}) {
  if (isConnectionUp(d2)) return { action: 'none' };
  if (first.failureClass === 'streamer-mute') {
    if (!(remoteCandidateCount(d2) > 0)) return { action: 'give-up' };
    const now = classifyConnectTimeout(d2);
    return now.failureMessage ? { action: 'message', message: now.failureMessage } : { action: 'none' };
  }
  return first.failureMessage ? { action: 'message', message: first.failureMessage } : { action: 'none' };
}

module.exports = {
  CLOSE_KIND,
  STREAMER_LOST_CODE,
  STREAMER_LOST_REASON,
  VIEWER_CLOSED_CODE,
  VIEWER_CLOSED_REASON,
  STREAMER_TIMEOUT_CODE,
  ADMISSION_REQUIRED_CODE,
  STREAMER_WAIT_BACKSTOP_MS,
  STREAMER_LOST_AUTO_RETRY_MIN_MS,
  STREAMER_LOST_AUTO_RETRY_MAX_MS,
  streamerLostAutoRetryDelay,
  isStreamerLost,
  isViewerClosed,
  classifyClose,
  shouldAutoRetry,
  terminateAction,
  keepsPinOnRetry,
  isConnectionUp,
  classifyConnectTimeout,
  armsConnectVerdict,
  connectTimeoutVerdict,
  isRetryableTicketFailure,
  streamerLostMessage,
  composeStreamerLostMessage,
  closedByViewerMessage,
  retryFailedMessage,
  gaveUpMessage,
  admissionRefusedMessage,
  gaveUpDetail,
  disconnectCardCopy,
};
